import type { ITypographyStyledCommon } from "@/types/common";
import applyResponsiveStyles from "@/utils/applyResponsiveStyles";
import tw, { css, styled } from "twin.macro";
import Paragraph from "../Paragraph";
import type { CSSProperties } from "styled-components";

interface IButtonStyled extends Omit<ITypographyStyledCommon, "$sizeVariant"> {
  disabled?: boolean;
  $fullWidth?: boolean;
  $rounded?: boolean;
  $justify?: CSSProperties["justifyContent"];
  [key: string]: any;
}

const buttonStyles = {
  base: tw`inline-flex items-center cursor-pointer
           transition-all ease-in-out duration-300 gap-2`,
  sizes: {
    button: {
      small: tw`py-xs px-s gap-xxs`,
      medium: tw`py-xs px-s gap-s`,
      large: tw`py-s px-sm gap-m`,
    },
    icon: {
      small: tw`w-4 h-4`,
      medium: tw`w-5 h-5`,
      large: tw`w-6 h-6`,
    },
  },

  colors: {
    primary: {
      default: {
        default: tw`text-primaryV2-40 hover:(bg-primaryV2-90)`,
        disabled: tw`text-neutral-80`,
      },
    },
    secondary: {
      default: {
        default: tw`text-secondary-40 hover:(bg-secondary-90)`,
        disabled: tw`text-neutral-80`,
      },
    },
    neutral: {
      default: {
        default: tw`text-neutral-10 hover:(text-neutral-0 bg-neutral-99)`,
        disabled: tw`text-neutral-80`,
      },
    },
  },
  fullWidth: tw`w-full`,
  disabled: tw`cursor-not-allowed`,
  rounded: {
    small: tw`rounded-s`,
    medium: tw`rounded-m`,
    large: tw`rounded-lg`,
  },
};

const iconStyles = {
  base: tw`fill-current`,
  path: tw`fill-current`,
};

export const StyledStartIcon = styled.span<Pick<IButtonStyled, "$size">>`
  & > svg {
    ${iconStyles.base}
    & > path {
      ${iconStyles.path}
    }

    ${({ $size }) =>
      applyResponsiveStyles({
        stylesBreakpoints: buttonStyles.sizes.icon,
        size: $size,
      })}
  }
`;

export const StyledWrapperContent = styled.div<Pick<IButtonStyled, "$justify">>`
  ${tw`flex w-full`}

  ${({ $justify }) => {
    return css`
      justify-content: ${$justify};
    `;
  }}
`;

export const StyledEndIcon = styled.span<Pick<IButtonStyled, "$size">>`
  & > svg {
    ${iconStyles.base}
    & > path {
      ${iconStyles.path}
    }

    ${({ $size }) =>
      applyResponsiveStyles({
        stylesBreakpoints: buttonStyles.sizes.icon,
        size: $size,
      })}
  }
`;

export const StyledParagraph = styled(Paragraph)`
  ${tw`text-current transition-none`}
`;

export const StyledWrapperBtnCategory = styled.button<IButtonStyled>`
  ${buttonStyles.base}

  ${({ $fullWidth }) => $fullWidth && buttonStyles.fullWidth}

  ${({ disabled }) => disabled && buttonStyles.disabled}

  ${({ $rounded }) =>
    $rounded &&
    applyResponsiveStyles({
      stylesBreakpoints: buttonStyles.rounded,
    })}

  ${({ $color, disabled }) =>
    buttonStyles.colors[$color ?? "primary"].default[disabled ? "disabled" : "default"]}


  ${({ $size }) =>
    applyResponsiveStyles({
      stylesBreakpoints: buttonStyles.sizes.button,
      size: $size,
    })}
`;
